import {
  getParticipantColor,
  getParticipantInitials,
} from "@/features/sharing/utils/participant-avatar"
import { cn } from "@/lib/utils"

/** Minimal shape shared by chat-session and project participants. Groups render
 * their display-name initials just like users (e.g. "ET" for "Engineering Team"). */
export type ParticipantLike = {
  oid?: string
  displayName?: string
}

type ParticipantAvatarProps = {
  participant: ParticipantLike
  size?: number
  ring?: boolean
  className?: string
}

export function ParticipantAvatar({
  participant,
  size = 18,
  ring = false,
  className,
}: ParticipantAvatarProps) {
  return (
    <span
      aria-hidden="true"
      title={participant.displayName}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white",
        ring && "ring-sidebar ring-2",
        className
      )}
      style={{
        width: size,
        height: size,
        backgroundColor: getParticipantColor(participant.oid || participant.displayName),
        fontSize: Math.round(size * 0.45),
      }}
    >
      {getParticipantInitials(participant.displayName)}
    </span>
  )
}

type ParticipantFacepileProps = {
  participants: ParticipantLike[]
  max?: number
  size?: number
  className?: string
}

export function ParticipantFacepile({
  participants,
  max = 3,
  size = 18,
  className,
}: ParticipantFacepileProps) {
  if (participants.length === 0) {
    return null
  }
  const shown = participants.slice(0, max)
  const overflow = participants.length - shown.length

  return (
    <span className={cn("flex shrink-0 items-center", className)}>
      {shown.map((participant, index) => (
        <ParticipantAvatar
          key={participant.oid || index}
          ring
          className={index > 0 ? "-ml-1.5" : undefined}
          participant={participant}
          size={size}
        />
      ))}
      {overflow > 0 && (
        <span
          aria-hidden="true"
          className="ring-sidebar text-muted-foreground bg-muted -ml-1.5 inline-flex shrink-0 items-center justify-center rounded-full font-semibold ring-2"
          style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
        >
          +{overflow}
        </span>
      )}
    </span>
  )
}
